import {
  hostname,
  ProposalReview,
  truncate,
  WebsiteProposalPanel,
  type ProposalChoice,
  type ProposalItem,
} from "@/components/molecules/website-proposal";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/shadcn-io/spinner";
import {
  useApplyEnrichment,
  useDismissEnrichment,
  useEnrichmentFinished,
  useEnrichmentState,
  useStartEnrichment,
} from "@/features/enrichment/hooks/use-enrichment";
import type {
  EnrichmentRun,
  EnrichmentScope,
  ProposedFaq,
  ProposedService,
} from "@/features/enrichment/types";
import i18n, { useTranslation } from "@/features/utils/i18n";
import { formatCurrency } from "@/features/utils/numbers";
import { ArrowsClockwiseIcon } from "@phosphor-icons/react";
import type { TFunction } from "i18next";
import { toast } from "sonner";

/**
 * The page half of « Remplir depuis mon site » on the FAQ and Services pages:
 * the reading in progress, then what it proposes, for the owner to tick,
 * reword and apply — nothing reaches the list before that.
 *
 * Shown above the list whatever started the reading (the Add dialog closes on
 * start), and only while there is something to say: a reading going, a
 * proposal waiting, a failure the owner has not closed yet. A run from before
 * the review, or one applied or dismissed, shows nothing.
 */
export function FastTrackReview({ scope }: { scope: EnrichmentScope }) {
  const { t } = useTranslation("fastTrack");
  const { data } = useEnrichmentState();
  const startRun = useStartEnrichment();
  const applyRun = useApplyEnrichment();
  const dismissRun = useDismissEnrichment();

  const run = data?.runs?.[scope] || null;

  useEnrichmentFinished(run, (finished) => {
    if (finished.status === "done") toast.success(t("toast.ready"));
  });

  if (!run) return null;

  const website = hostname(run.url || data?.website || "");
  const isRunning = run.status === "running";
  const isPending = run.review?.[scope] === "pending";
  if (!isRunning && !isPending) return null;

  const canRestart = !!data?.website && !!data?.scopes?.includes(scope);
  const busy = applyRun.isPending || dismissRun.isPending;

  const handleRestart = async () => {
    try {
      await startRun.mutateAsync({ scopes: [scope] });
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t("toast.startError"),
      );
    }
  };

  const handleDismiss = async () => {
    try {
      await dismissRun.mutateAsync({ runId: run.id, scope });
    } catch {
      toast.error(t("toast.reviewError"));
    }
  };

  const handleApply = async (choices: ProposalChoice[]) => {
    try {
      const applied = await applyRun.mutateAsync({
        runId: run.id,
        scope,
        items: choices.map(({ key, edits }) => ({ key, ...edits })),
      });
      const summary = applied.summary?.[scope];
      const written = (summary?.created || 0) + (summary?.updated || 0);
      toast.success(
        written > 0
          ? t("toast.applied", {
              created: summary?.created || 0,
              updated: summary?.updated || 0,
            })
          : t("toast.appliedNothing"),
      );
    } catch {
      // The server's reason is technical and in English; the page refetches
      // on failure, so what is on screen afterwards is the truth.
      toast.error(t("toast.reviewError"));
    }
  };

  const restart = canRestart ? (
    <Button
      type="button"
      variant="outline"
      onClick={handleRestart}
      disabled={busy || startRun.isPending}
    >
      <ArrowsClockwiseIcon className="h-4 w-4" weight="bold" />
      {t("actions.again")}
    </Button>
  ) : null;

  if (isRunning) {
    return (
      <WebsiteProposalPanel
        title={t(`scopes.${scope}.title`)}
        description={
          <span className="inline-flex items-center gap-2">
            <Spinner className="h-4 w-4" />
            {t("status.running", { website })}
          </span>
        }
      />
    );
  }

  if (run.status === "failed") {
    return (
      <WebsiteProposalPanel
        title={t(`scopes.${scope}.title`)}
        description={t(`scopes.${scope}.description`, { website })}
        // The server's reason is technical and in English: it is in the logs,
        // the owner only needs to know nothing changed.
        error={t("status.failed")}
        action={
          <div className="flex gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={handleDismiss}
              disabled={busy}
            >
              {t("actions.close")}
            </Button>
            {restart}
          </div>
        }
      />
    );
  }

  return (
    <WebsiteProposalPanel
      title={t(`scopes.${scope}.title`)}
      description={t("status.found", { website })}
      action={restart}
    >
      <ProposalReview
        // A new reading is a new proposal: start its selection from scratch.
        key={run.id}
        intro={t(`review.${scope}.intro`)}
        nothingNew={t("status.nothingNew")}
        hint={t(`review.${scope}.hint`)}
        items={proposalItems(run, scope, t)}
        busy={busy}
        onApply={handleApply}
        onDismiss={handleDismiss}
      />
    </WebsiteProposalPanel>
  );
}

const proposalItems = (
  run: EnrichmentRun,
  scope: EnrichmentScope,
  t: TFunction,
): ProposalItem[] =>
  scope === "faq"
    ? (run.proposal?.faq || []).map((item) => faqItem(item, t))
    : scope === "services"
      ? (run.proposal?.services || []).map((item) => serviceItem(item, t))
      : [];

/** "Nouvelle" / "Mise à jour", beside the item's name. */
const Tag = ({ children }: { children: string }) => (
  <span className="text-muted-foreground ml-2 text-xs font-normal">
    {children}
  </span>
);

const quoted = (value: string) => `« ${truncate(value)} »`;

const faqItem = (item: ProposedFaq, t: TFunction): ProposalItem => {
  if (item.action === "create") {
    return {
      key: item.key,
      label: (values) => (
        <>
          {values.question}
          <Tag>{t("review.tags.new")}</Tag>
        </>
      ),
      value: (values) => values.answer,
      note: item.category
        ? t("review.faq.category", { category: item.category })
        : undefined,
      fields: [
        {
          name: "question",
          label: t("review.faq.question"),
          value: item.question,
        },
        {
          name: "answer",
          label: t("review.faq.answer"),
          value: item.answer,
          multiline: true,
        },
      ],
    };
  }

  const current = item.current;
  const answers = item.changes.includes("answer");
  const notes: string[] = [];
  if (answers) {
    notes.push(
      !current?.answer
        ? t("review.currentlyEmpty")
        : current.refined
          ? t("review.faq.keepsRefined", { value: quoted(current.answer) })
          : t("review.replaces", { value: quoted(current.answer) }),
    );
  }
  if (item.changes.includes("question")) {
    notes.push(
      t("review.faq.newPhrasing", { question: quoted(item.question) }),
    );
  }
  if (item.changes.includes("category")) {
    notes.push(t("review.faq.category", { category: item.category }));
  }

  return {
    key: item.key,
    label: (
      <>
        {current?.question || item.question}
        <Tag>{t("review.tags.update")}</Tag>
      </>
    ),
    value: answers
      ? (values: Record<string, string>) => values.answer
      : item.changes.includes("question")
        ? item.question
        : item.category,
    note: notes.join(" · "),
    fields: answers
      ? [
          {
            name: "answer",
            label: t("review.faq.answer"),
            value: item.answer,
            multiline: true,
          },
        ]
      : undefined,
  };
};

// In the language the review is read in, not the browser's.
const formatPrice = (price: number) =>
  formatCurrency(price, i18n.language, "EUR");

const serviceItem = (item: ProposedService, t: TFunction): ProposalItem => {
  const duration = (minutes: number) =>
    t("review.services.duration", { minutes });

  if (item.action === "create") {
    const facts = [
      item.duration_minutes ? duration(item.duration_minutes) : "",
      item.price ? formatPrice(item.price) : "",
    ].filter(Boolean);

    return {
      key: item.key,
      label: (values) => (
        <>
          {values.name}
          <Tag>{t("review.tags.new")}</Tag>
        </>
      ),
      value: (values) =>
        [values.description, facts.join(" · ")].filter(Boolean).join("\n") ||
        t("review.services.noDetails"),
      fields: [
        { name: "name", label: t("review.services.name"), value: item.name },
        {
          name: "description",
          label: t("review.services.description"),
          value: item.description,
          multiline: true,
        },
      ],
    };
  }

  const current = item.current;
  const describes = item.changes.includes("description");
  const facts = [
    item.changes.includes("duration") && item.duration_minutes
      ? duration(item.duration_minutes)
      : "",
    item.changes.includes("price") && item.price ? formatPrice(item.price) : "",
  ].filter(Boolean);

  // What the update overwrites, in the same order as what it proposes.
  const replaced = [
    describes && current?.description ? quoted(current.description) : "",
    item.changes.includes("duration") && current?.duration
      ? duration(current.duration)
      : "",
    item.changes.includes("price") && current?.price
      ? formatPrice(current.price)
      : "",
  ].filter(Boolean);

  return {
    key: item.key,
    label: (
      <>
        {current?.name || item.name}
        <Tag>{t("review.tags.update")}</Tag>
      </>
    ),
    value: (values) =>
      [describes ? values.description : "", facts.join(" · ")]
        .filter(Boolean)
        .join("\n"),
    note: replaced.length
      ? t("review.replaces", { value: replaced.join(", ") })
      : t("review.currentlyEmpty"),
    fields: describes
      ? [
          {
            name: "description",
            label: t("review.services.description"),
            value: item.description,
            multiline: true,
          },
        ]
      : undefined,
  };
};
